import { formatEur } from './charts/IbcsTooltip';
import type { FxExposureData } from '../../types/cashCockpit';

interface Props {
    data: FxExposureData;
    shockPct: number;
    onShockChange: (n: number) => void;
}

export default function FxExposureTab({ data, shockPct, onShockChange }: Props) {
    const rows = data.data;
    const entities = Array.from(new Set(rows.map(r => r.companyCode))).sort();
    const currencies = Array.from(new Set(rows.map(r => r.currencyCode))).sort();
    const byKey = new Map<string, typeof rows[number]>();
    rows.forEach(r => byKey.set(`${r.companyCode}|${r.currencyCode}`, r));
    const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.amountEUR)));

    const totalShock = rows.reduce((s, r) => s + Math.abs(r.shockDeltaEUR), 0);

    return (
        <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                <label className="text-sm text-gray-700">FX shock scenario:</label>
                <input
                    type="range" min={0} max={20} step={0.5}
                    value={shockPct}
                    onChange={e => onShockChange(parseFloat(e.target.value))}
                    className="flex-1 max-w-xs"
                />
                <span className="font-mono text-sm font-semibold min-w-[3rem]">±{shockPct}%</span>
                <div className="ml-auto text-xs text-gray-500">
                    Aggregate EUR sensitivity: <span className="font-semibold text-gray-900">{formatEur(totalShock)}</span>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">
                    FX Exposure Heatmap (EUR equivalent)
                </h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-3 py-2 text-left">Entity</th>
                                {currencies.map(c => (
                                    <th key={c} className="px-2 py-2 text-right font-mono">{c}</th>
                                ))}
                                <th className="px-2 py-2 text-right">Row Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entities.map(e => {
                                const rowTotal = currencies.reduce((sum, c) => sum + (byKey.get(`${e}|${c}`)?.amountEUR ?? 0), 0);
                                return (
                                    <tr key={e} className="border-t border-gray-100">
                                        <td className="px-3 py-1.5 font-semibold uppercase">{e}</td>
                                        {currencies.map(c => {
                                            const v = byKey.get(`${e}|${c}`);
                                            const amt = v?.amountEUR ?? 0;
                                            const intensity = Math.min(1, Math.abs(amt) / maxAbs);
                                            const bg = `rgba(31, 41, 55, ${intensity * 0.3})`;
                                            return (
                                                <td
                                                    key={c}
                                                    className="px-2 py-1.5 text-right font-mono tabular-nums"
                                                    style={{ backgroundColor: v ? bg : 'transparent' }}
                                                    title={v ? `Shock ±${shockPct}%: ${formatEur(v.shockDownEUR)} / ${formatEur(v.shockUpEUR)}` : ''}
                                                >
                                                    {v ? formatEur(amt) : '—'}
                                                </td>
                                            );
                                        })}
                                        <td className="px-2 py-1.5 text-right font-mono font-semibold">{formatEur(rowTotal)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="text-xs text-gray-500 px-4 py-2 bg-gray-50 border-t border-gray-200">
                    Hover a cell to see shock ±{shockPct}% bandwidth.
                </div>
            </div>
        </div>
    );
}
